import React from 'react'
import {motion} from "motion/react"
import {BookOpen} from "lucide-react";
import {Box, Card, ColorPaletteProp, Stack, Typography, useTheme} from "@mui/joy";
import {useUITheme} from "../../hooks/useUITheme";
import {useTranslation} from "react-i18next";


const LoadingPageComponentSmall = (props: { label?: string, loadingText?: string, color?: ColorPaletteProp }) => {
    const theme = useTheme()
    const {t} = useTranslation()
    return <Stack overflow={"hidden"}>
        <Stack minHeight={"30dvh"} width={"100%"} justifyContent={"center"} overflow={"hidden"} alignItems={"center"}
               zIndex={50}>

            {/* Main Loader Container */}
            <Stack position={"relative"} zIndex={10} alignItems={"center"}>
                {/* Simple Book Animation */}
                <Stack position={"relative"} marginBottom={1}>
                    {/* Animated Book Icon */}
                    <motion.div
                        animate={{
                            scale: [1, 1.1, 1],
                        }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}
                        style={{
                            position: "relative",
                        }}>
                        <motion.div
                            animate={{
                                rotate: [0, -10, 0, 10, 0],
                            }}
                            transition={{
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}>
                            <BookOpen style={{
                                fontSize: 70,
                                color: theme.palette[props.color || "primary"]['500']
                            }}/>
                        </motion.div>
                    </motion.div>

                    {/* Floating Pages */}
                    <Stack position={"absolute"} width={32}
                           top={8} left={"50%"}
                           style={{
                               transform: 'translateX(-50%)',
                           }}>
                        {[0, 1, 2, 3].map((index) => (
                            <motion.div
                                key={index}
                                animate={{
                                    y: [0, -30, -60],
                                    opacity: [0, 1, 0],
                                    x: [0, (index - 1.5) * 10, (index - 1.5) * 20],
                                }}
                                transition={{
                                    duration: 2.5,
                                    repeat: Infinity,
                                    ease: "easeOut",
                                    delay: index * 0.2,
                                }}
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    translateX: "-50%",
                                    top: 0
                                }}>
                                <Card color={props.color} sx={{
                                    width: 20,
                                    height: 30,
                                    boxShadow: "lg"
                                }}>
                                    {/* Page lines */}
                                    <Stack width={"100%"} gap={"6px"}>
                                        {[1, 2, 3, 4,].map((line) => (
                                            <Box
                                                height={"0.1px"}
                                                borderRadius={20}
                                                sx={{
                                                    backgroundColor: "neutral.300",
                                                }}
                                                key={line}
                                                style={{width: `${120 - line * 10}%`}}
                                            />
                                        ))}
                                    </Stack>
                                </Card>
                            </motion.div>
                        ))}
                    </Stack>

                </Stack>

                {/* Loading text */}
                <motion.div
                    initial={{opacity: 0, y: 10}}
                    animate={{opacity: 1, y: 0}}
                    transition={{delay: 0.3}}>
                    <Typography textAlign={"center"}
                                level={"title-md"}>{props.loadingText || `Loading ${props.label}`}</Typography>
                    <motion.p
                        animate={{
                            opacity: [0.5, 1, 0.5],
                        }}
                        transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            ease: "easeInOut"
                        }}>
                        {!props.loadingText && <Typography textAlign={"center"} color={"neutral"}>
                            {t("loadingPageText")}
                        </Typography>}
                    </motion.p>
                </motion.div>

                {/* Progress dots */}
                <Stack marginTop={1} gap={2} direction={"row"}>
                    {[0, 1, 2].map((index) => (
                        <motion.div
                            key={index}
                            animate={{
                                scale: [1, 1.5, 1],
                                opacity: [0.3, 1, 0.3],
                            }}
                            transition={{
                                duration: 1.5,
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: index * 0.2,
                            }}
                            style={{
                                width: 7,
                                height: 7,
                                backgroundColor: theme.palette[props.color || "primary"][500],
                                borderRadius: 10,
                            }}
                        />
                    ))}
                </Stack>
            </Stack>
        </Stack>
    </Stack>
}

export default LoadingPageComponentSmall